import React from "react";
import { Card } from "components/ui/BootstrapCompat";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useQuery } from "react-query";
import useLocalStorage from "helpers/useLocalStorage";
import { donateWithdrawGetMany } from "services/withdraw.service";
import { userWalletGetMe } from "services/user.service";
import {
  buildMonthlyNetTotals,
  formatCurrency,
  normalizeWithdrawRequests,
} from "../withdraw.utils";

export default function WithdrawMonthlyChartCard() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");

  const { data: walletData, isLoading: isLoadingWallet } = useQuery(
    ["wallet-me", businessId],
    async () => {
      if (!token) return null;
      return await userWalletGetMe({ token, businessId });
    },
    { enabled: !!token },
  );

  const { data: withdrawData, isLoading: isLoadingHistory } = useQuery(
    ["withdraw-get-many", businessId || "streamer"],
    async () => {
      if (!token) return [];
      const response = await donateWithdrawGetMany({
        token,
      });
      return normalizeWithdrawRequests(response);
    },
    { enabled: !!token, retry: false },
  );

  const history = Array.isArray(withdrawData) ? withdrawData : [];
  const currencyLabel = String(
    (walletData as any)?.data?.currency || (walletData as any)?.currency || "ກີບ",
  );
  const monthlyChartData = React.useMemo(
    () => buildMonthlyNetTotals(history),
    [history],
  );

  const isLoading = isLoadingWallet || isLoadingHistory;

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="mb-1 text-lg font-semibold text-primary">
              ສະຖິຕິການຖອນລາຍເດືອນ
            </h2>
            <p className="mb-0 text-sm text-gray-500">
              ລວມຍອດສຸດທິ (ຫຼັງຫັກຄ່າທຳນຽມ)
            </p>
          </div>
        </div>

        <div className="mt-3 h-72 w-full">
          {isLoading ? (
            <div className="flex h-full items-center justify-center rounded border border-dashed border-gray-200 text-sm text-gray-500">
              ກຳລັງໂຫລດຂໍ້ມູນ...
            </div>
          ) : monthlyChartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis
                  tickFormatter={(value) => {
                    const numericValue = Number(value);
                    if (!Number.isFinite(numericValue)) return "";
                    if (numericValue === 0) return "0";
                    if (Math.abs(numericValue) < 1000) return `${numericValue}`;
                    if (Math.abs(numericValue) < 1_000_000)
                      return `${Math.round(numericValue / 1_000)}K`;
                    return `${Math.round(numericValue / 1_000_000)}M`;
                  }}
                />
                <Tooltip
                  formatter={(value: any) => [
                    `${formatCurrency(Number(value))} ${currencyLabel}`,
                    "ຍອດສຸດທິ",
                  ]}
                />
                <Bar dataKey="netTotal" fill="#0d6efd" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center rounded border border-dashed border-gray-200 text-sm text-gray-500">
              ຍັງບໍ່ມີຂໍ້ມູນສະຖິຕິ
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
